import { SchemaPath, validate } from "@angular/forms/signals";
import { S25ItemI } from "../../../../pojo/S25ItemI";
import { S25Util } from "../../../../util/s25-util";
import { SignalFormUtil } from "../../../../util/signal.form.util";

import type { OrgScopeMode, QuotaScopeItem, WindowMode } from "./event-quota.model";
import { EDITOR_DURATION_TYPES, NAME_MAX_LENGTH, TypeIds, windowModeNameMap } from "./event-quota.model";
import {
    exceedsFixedWindowSpanLimit,
    exceedsForwardFromTodaySpanLimit,
    fixedWindowSpanHelpText,
    hasMeaningfulMaxTimeHoursMinutes,
    maxRollingDurationForUnit,
    normalizeWindowMode,
    rollingDurationLimitMessage,
    toQuotaTypeId,
    usesDurationForWindow,
    usesFixedDatesForWindow,
} from "./event-quota.util";

export namespace EventQuotaFormValidators {
    export function itemName(path: SchemaPath<string>, options?: { message?: string }) {
        SignalFormUtil.maxLengthValidator(path, NAME_MAX_LENGTH, options);
    }

    export function duration(
        path: SchemaPath<number | null>,
        windowModePath: SchemaPath<WindowMode>,
        durationTypePath: SchemaPath<string>,
        options?: { message?: string },
    ) {
        validate(path, ({ value, stateOf }) => {
            if (!usesDurationForWindow(stateOf(windowModePath).value())) {
                return;
            }
            const durationValue = value();
            if (durationValue == null || durationValue < 1) {
                return {
                    kind: "required",
                    message: options?.message ?? "Please enter a rolling period length.",
                };
            }
            const durationType = stateOf(durationTypePath).value();
            const maxDuration = maxRollingDurationForUnit(durationType);
            if (maxDuration != null && durationValue > maxDuration) {
                return {
                    kind: "max",
                    message: options?.message ?? rollingDurationLimitMessage(durationType),
                };
            }
        });
    }

    export function durationType(
        path: SchemaPath<string>,
        windowModePath: SchemaPath<WindowMode>,
        options?: { message?: string },
    ) {
        validate(path, ({ value, stateOf }) => {
            if (!usesDurationForWindow(stateOf(windowModePath).value())) {
                return;
            }
            const durationTypeValue = value();
            if (!EDITOR_DURATION_TYPES.includes(durationTypeValue as (typeof EDITOR_DURATION_TYPES)[number])) {
                return {
                    kind: "invalid",
                    message: options?.message ?? "Please select a duration unit.",
                };
            }
        });
    }

    export function quotaStartDt(
        path: SchemaPath<Date | null>,
        windowModePath: SchemaPath<WindowMode>,
        options?: { message?: string },
    ) {
        validate(path, ({ value, stateOf }) => {
            if (normalizeWindowMode(stateOf(windowModePath).value()) !== "fixed") {
                return;
            }
            const startDate = value();
            if (!startDate || isNaN(startDate.getTime())) {
                return {
                    kind: "required",
                    message: options?.message ?? "Please enter a start date.",
                };
            }
        });
    }

    export function quotaEndDt(
        path: SchemaPath<Date | null>,
        windowModePath: SchemaPath<WindowMode>,
        quotaStartDtPath: SchemaPath<Date | null>,
        options?: { message?: string },
    ) {
        validate(path, ({ value, stateOf }) => {
            const windowMode = normalizeWindowMode(stateOf(windowModePath).value());
            if (!usesFixedDatesForWindow(windowMode)) {
                return;
            }
            const endDate = value();
            if (!endDate || isNaN(endDate.getTime())) {
                return {
                    kind: "required",
                    message: options?.message ?? "Please enter an end date.",
                };
            }
            if (windowMode === "fixed") {
                const startDate = stateOf(quotaStartDtPath).value();
                if (startDate && !isNaN(startDate.getTime())) {
                    const endDateStr = S25Util.date.toS25ISODateStr(endDate);
                    const startDateStr = S25Util.date.toS25ISODateStr(startDate);
                    if (endDateStr && startDateStr && endDateStr <= startDateStr) {
                        return {
                            kind: "invalid",
                            message: options?.message ?? "End date must be after the start date.",
                        };
                    }
                    if (exceedsFixedWindowSpanLimit(startDate, endDate)) {
                        return {
                            kind: "max",
                            message: options?.message ?? fixedWindowSpanHelpText(),
                        };
                    }
                }
            } else if (windowMode === "today" && exceedsForwardFromTodaySpanLimit(endDate)) {
                return {
                    kind: "max",
                    message: options?.message ?? fixedWindowSpanHelpText(),
                };
            }
        });
    }

    export function windowMode(path: SchemaPath<WindowMode>, options?: { message?: string }) {
        validate(path, ({ value }) => {
            const windowModeValue = value();
            if (!windowModeValue || !(windowModeValue in windowModeNameMap)) {
                return {
                    kind: "invalid",
                    message: options?.message ?? "Please select a period direction.",
                };
            }
        });
    }

    export function maxTimeMinutes(path: SchemaPath<number | null>, options?: { message?: string }) {
        validate(path, ({ value }) => {
            const minutesValue = value();
            if (minutesValue == null) {
                return;
            }
            if (!Number.isInteger(minutesValue) || minutesValue < 0 || minutesValue > 59) {
                return {
                    kind: "invalid",
                    message: options?.message ?? "Minutes must be between 0 and 59.",
                };
            }
        });
    }

    export function requiresAtLeastOneLimit(
        quantityPath: SchemaPath<number | null>,
        maxTimeHoursPath: SchemaPath<number | null>,
        maxTimeMinutesPath: SchemaPath<number | null>,
        countByEventPath: SchemaPath<boolean>,
        options?: { message?: string },
    ) {
        const message = options?.message ?? "Set maximum occurrences and/or maximum total booked time.";
        const countByEventMessage = "Count by event quotas require a maximum event limit.";
        const limitError = (
            quantity: number | null,
            maxTimeHours: number | null,
            maxTimeMinutes: number | null,
            countByEvent: boolean,
        ) => {
            if (countByEvent) {
                if (!quantity || quantity <= 0) {
                    return { kind: "required", message: countByEventMessage };
                }
                return;
            }
            if ((!quantity || quantity === 0) && !hasMeaningfulMaxTimeHoursMinutes(maxTimeHours, maxTimeMinutes)) {
                return { kind: "required", message };
            }
        };

        const runLimitCheck = ({ stateOf }: { stateOf: <T>(path: SchemaPath<T>) => { value: () => T } }) =>
            limitError(
                stateOf(quantityPath).value(),
                stateOf(maxTimeHoursPath).value(),
                stateOf(maxTimeMinutesPath).value(),
                stateOf(countByEventPath).value(),
            );

        validate(quantityPath, runLimitCheck);
        validate(countByEventPath, runLimitCheck);
    }

    export function countByEventDisallowsMaxTime(
        countByEventPath: SchemaPath<boolean>,
        maxTimeHoursPath: SchemaPath<number | null>,
        maxTimeMinutesPath: SchemaPath<number | null>,
        options?: { message?: string },
    ) {
        const message =
            options?.message ??
            "Maximum booked time cannot be used with count by event. Turn off count by event or clear the time limit.";
        validate(maxTimeHoursPath, ({ value, stateOf }) => {
            if (
                stateOf(countByEventPath).value() &&
                hasMeaningfulMaxTimeHoursMinutes(value(), stateOf(maxTimeMinutesPath).value())
            ) {
                return { kind: "invalid", message };
            }
        });
        validate(maxTimeMinutesPath, ({ value, stateOf }) => {
            if (
                stateOf(countByEventPath).value() &&
                hasMeaningfulMaxTimeHoursMinutes(stateOf(maxTimeHoursPath).value(), value())
            ) {
                return { kind: "invalid", message };
            }
        });
        validate(countByEventPath, ({ stateOf }) => {
            if (
                stateOf(countByEventPath).value() &&
                hasMeaningfulMaxTimeHoursMinutes(stateOf(maxTimeHoursPath).value(), stateOf(maxTimeMinutesPath).value())
            ) {
                return { kind: "invalid", message };
            }
        });
    }

    export function organizationsWhenOrgType(
        organizationsPath: SchemaPath<S25ItemI[]>,
        quotaTypeItemIdPath: SchemaPath<string>,
        orgScopeModePath: SchemaPath<OrgScopeMode>,
        organizationSearchPath: SchemaPath<QuotaScopeItem | null>,
        options?: { message?: string },
    ) {
        const validateOrgScope = ({ stateOf }: { stateOf: <T>(path: SchemaPath<T>) => { value: () => T } }) => {
            const typeId = toQuotaTypeId(stateOf(quotaTypeItemIdPath).value());
            if (typeId !== TypeIds.Organization) {
                return;
            }
            const scopeMode = stateOf(orgScopeModePath).value();
            if (scopeMode === "search") {
                if (!stateOf(organizationSearchPath).value()?.itemId) {
                    return {
                        kind: "required",
                        message:
                            options?.message ??
                            "Organization quotas require an organization search when using search scope.",
                    };
                }
                return;
            }
            if (!(stateOf(organizationsPath).value()?.length ?? 0)) {
                return {
                    kind: "required",
                    message:
                        options?.message ??
                        "Organization quotas require at least one organization when using specific organizations.",
                };
            }
        };

        validate(organizationsPath, validateOrgScope);
        validate(orgScopeModePath, validateOrgScope);
        validate(organizationSearchPath, validateOrgScope);
    }

    export function daySelection(path: SchemaPath<boolean[]>, options?: { message?: string }) {
        validate(path, ({ value }) => {
            const selected = value();
            if (!selected?.some((dayOn) => dayOn)) {
                return {
                    kind: "required",
                    message: options?.message ?? "Select at least one day, or turn all days on.",
                };
            }
        });
    }

    export function allowedTimeBand(
        startPath: SchemaPath<string | null>,
        endPath: SchemaPath<string | null>,
        limitToTimePath: SchemaPath<boolean>,
        options?: { message?: string },
    ) {
        const validateBand = ({ stateOf }: { stateOf: <T>(path: SchemaPath<T>) => { value: () => T } }) => {
            if (!stateOf(limitToTimePath).value()) {
                return;
            }
            const start = stateOf(startPath).value()?.trim();
            const end = stateOf(endPath).value()?.trim();
            if (!start || !end) {
                return {
                    kind: "required",
                    message: options?.message ?? "Enter both a start time and an end time.",
                };
            }
            if (start >= end) {
                return {
                    kind: "invalid",
                    message: options?.message ?? "End time must be after the start time.",
                };
            }
        };
        validate(startPath, validateBand);
        validate(endPath, validateBand);
        validate(limitToTimePath, validateBand);
    }

    export function uniqueItemName(path: SchemaPath<string>, options: { takenNames: () => Set<string> }) {
        validate(path, ({ value }) => {
            const name = value()?.trim();
            if (!name) {
                return;
            }
            if (options.takenNames().has(name)) {
                return {
                    kind: "duplicate",
                    message: "This name is already in use. Please choose another name.",
                };
            }
        });
    }
}
